/**
 * `IDemoOrchestratorClient` — Phase 5 Phase D Patient PHG view demo
 * orchestrator client interface per CPL Phase 5 Phase D IMPL composition
 * guide pair-with-DEV5 §4.2 (CSO/Product/cpl-phase-5-phase-d-impl-
 * composition-guide-pair-with-dev5.md, 2026-05-14).
 *
 * The UC22 federated prior-auth demo orchestrator emits a Server-Sent
 * Events (SSE) stream over the lifecycle of a single demo run; the
 * patient PHG view subscribes to a run's stream to drive a 5-phase
 * timeline + Authority Indicator state machine in real time.
 *
 * # Doctrinal anchors preserved
 *
 *   - Patient-fiduciary doctrine — pa-tracker real-time visibility
 *     surfaces the doctrine as UI; the orchestrator stream IS the
 *     mechanism by which the patient sees substrate routing happen.
 *   - NO-SHT-BACKDOOR — event payloads carry routing metadata +
 *     payer-rendered decision status; no cleartext PHI flows over the
 *     SSE channel at sandbox tier (Q3 2026 production-tier trajectory
 *     extends this with substrate-as-router discipline preserved).
 *   - Hub payload-blindness — the orchestrator emits status/phase/
 *     narrative metadata; clinical rationale rendering composes
 *     client-side at participant-view layer per Pattern B.
 *
 * # Sandbox-grade scope-fence
 *
 * v0 ships the interface + a deterministic `MockDemoOrchestratorClient`
 * for dev/test (canonical knee-MRI scenario pre-seeded). Production
 * mount uses `EventSource` browser API against
 * `GET /v0/demo/uc22/runs/:runId/stream` with sandbox-auth-backend
 * JWT session cookie composition per CTO design memo §3.1. The
 * production client lands in a follow-on PR composing with DEV6's
 * demo-orchestrator HTTP service when the substrate-side IMPL ships.
 */
const MOCK_SCENARIOS = {
    'knee-mri': {
        providerName: 'Memorial Hospital',
        payerName: 'BlueShield Plan',
        scenarioSummary: 'Knee MRI for suspected ACL tear',
        events: [
            { afterMs: 0, type: 'phase.started', phase: 'provider-initiated',
                narrative: 'Provider submitted a prior auth request.' },
            { afterMs: 600, type: 'pa.status_change', status: 'pending' },
            { afterMs: 1200, type: 'federation.query', phase: 'substrate-routed',
                narrative: 'Smart Health Network routed clinical evidence to the payer.' },
            { afterMs: 1800, type: 'pa.status_change', status: 'in-review' },
            { afterMs: 3000, type: 'authz.decision', phase: 'payer-adjudicating',
                narrative: 'The payer is applying clinical policy criteria.' },
            { afterMs: 4400, type: 'pa.status_change', status: 'approved' },
            { afterMs: 4400, type: 'audit.signed', phase: 'audit-published',
                narrative: 'The decision is multi-party signed and appended to the audit chain.' },
            { afterMs: 4800, type: 'phase.completed', phase: 'complete',
                narrative: 'Run complete.' },
        ],
    },
    'cardiac-imaging': {
        providerName: 'Stanford Health',
        payerName: 'United Plan',
        scenarioSummary: 'Cardiac CT angiography',
        events: [
            { afterMs: 0, type: 'phase.started', phase: 'provider-initiated' },
            { afterMs: 800, type: 'pa.status_change', status: 'pending' },
            { afterMs: 1600, type: 'federation.query', phase: 'substrate-routed' },
            { afterMs: 2400, type: 'pa.status_change', status: 'in-review' },
            { afterMs: 4000, type: 'authz.decision', phase: 'payer-adjudicating' },
            { afterMs: 5200, type: 'pa.status_change', status: 'denied' },
            { afterMs: 5200, type: 'audit.signed', phase: 'audit-published' },
            { afterMs: 5600, type: 'phase.completed', phase: 'complete' },
        ],
    },
    'precision-pa': {
        providerName: 'UCSF Health',
        payerName: 'Aetna Plan',
        scenarioSummary: 'Tumor-board-derived targeted therapy',
        events: [
            { afterMs: 0, type: 'phase.started', phase: 'provider-initiated' },
            { afterMs: 500, type: 'pa.status_change', status: 'in-review' },
            { afterMs: 2000, type: 'federation.query', phase: 'substrate-routed' },
            { afterMs: 3500, type: 'authz.decision', phase: 'payer-adjudicating' },
            { afterMs: 4500, type: 'pa.status_change', status: 'approved' },
            { afterMs: 4500, type: 'audit.signed', phase: 'audit-published' },
            { afterMs: 5000, type: 'phase.completed', phase: 'complete' },
        ],
    },
};
export class MockDemoOrchestratorClient {
    timerScheduler;
    runIdSequence;
    launchCounter = 0;
    runScenarios = new Map();
    constructor(options) {
        this.timerScheduler = options?.timerScheduler ?? 'real-time';
        this.runIdSequence = options?.runIdSequence ?? [];
    }
    launchRun = async (scenario) => {
        const idx = this.launchCounter;
        this.launchCounter += 1;
        const runId = idx < this.runIdSequence.length
            ? this.runIdSequence[idx]
            : `mock_run_${scenario}_${String(idx)}`;
        this.runScenarios.set(runId, scenario);
        return { runId };
    };
    subscribeRun = (runId, callbacks) => {
        const scenario = this.runScenarios.get(runId) ?? 'knee-mri';
        const script = MOCK_SCENARIOS[scenario];
        let cancelled = false;
        const timers = [];
        const fireScript = () => {
            if (cancelled)
                return;
            callbacks.onOpen();
            // Always emit one composed run-summary event first so the
            // container can populate provider/payer/scenarioSummary fields.
            if (!cancelled) {
                callbacks.onEvent({
                    ts: new Date().toISOString(),
                    type: 'phase.started',
                    providerName: script.providerName,
                    payerName: script.payerName,
                    scenarioSummary: script.scenarioSummary,
                });
            }
            for (const ev of script.events) {
                const dispatch = () => {
                    if (cancelled)
                        return;
                    callbacks.onEvent({
                        ts: new Date().toISOString(),
                        type: ev.type,
                        ...(ev.phase !== undefined ? { phase: ev.phase } : {}),
                        ...(ev.narrative !== undefined ? { narrative: ev.narrative } : {}),
                        ...(ev.status !== undefined ? { status: ev.status } : {}),
                    });
                    if (ev.type === 'phase.completed') {
                        callbacks.onClose();
                    }
                };
                if (this.timerScheduler === 'sync') {
                    dispatch();
                }
                else {
                    timers.push(setTimeout(dispatch, ev.afterMs));
                }
            }
        };
        if (this.timerScheduler === 'sync') {
            // Defer to next microtask so callers can attach state before events fire.
            queueMicrotask(fireScript);
        }
        else {
            timers.push(setTimeout(fireScript, 0));
        }
        return {
            unsubscribe: () => {
                cancelled = true;
                for (const t of timers)
                    clearTimeout(t);
            },
        };
    };
}
//# sourceMappingURL=demo-orchestrator-client.js.map